// staffEffects — pure selectors that turn the staff roster into gameplay
// numbers. Extracted from constants scattered across MaintenanceSystem,
// RosterSystem and DemandSystem so every staff effect lives in one place
// (and the modifier engine can wrap these as a source later).
//
// PARITY CONTRACT: for a migrated legacy save (any staff, any happiness)
// every value returned here must equal the pre-refactor inline math. New
// sub-skill-driven behavior must only ADD on top (e.g. the coach's tactica
// growth bonus), never change the legacy numbers.

import type { Staff, StaffType } from '@/config/staffConfig'

/** Unhappy staff work below their skill level (down to 50% at 0 happiness).
 *  Moved verbatim from MaintenanceSystem. */
export function effectiveSkill(skill: number, happiness: number): number {
  return skill * (0.5 + happiness / 200)
}

/** A member's sub-skill, morale-weighted; falls back to the overall skill. */
export function effectiveSubSkill(member: Staff, key: string): number {
  return effectiveSkill(member.skills?.[key] ?? member.skill, member.happiness)
}

function ofType(staff: Staff[], type: StaffType): Staff[] {
  return staff.filter(s => s.type === type)
}

function bestRawSubSkill(staff: Staff[], type: StaffType, key: string): number {
  return ofType(staff, type).reduce((max, s) => Math.max(max, s.skills?.[key] ?? s.skill), 0)
}

export interface CoachEffects {
  /** Monthly chance a young player grows (legacy: 0.5 + best skill / 200) */
  growthChance: number
  /** Max points per growth roll: 2 legacy; 3 with an elite tactician (≥70) */
  growthRollMax: number
  /** A tactically capable coach (≥60) reads potential exactly (scouting) */
  scouting: boolean
}

export function getCoachEffects(staff: Staff[]): CoachEffects {
  // Parity: the legacy formula used the coach's RAW overall skill
  const bestCoachSkill = ofType(staff, 'coach').reduce((max, c) => Math.max(max, c.skill), 0)
  const bestTactics = bestRawSubSkill(staff, 'coach', 'tactica')
  return {
    growthChance: 0.5 + bestCoachSkill / 200,
    growthRollMax: bestTactics >= 70 ? 3 : 2,
    scouting: bestTactics >= 60,
  }
}

export interface NegotiationEffects {
  /** Chance a signing-fee haggle succeeds (0.35 base, up to 0.85 with an
   *  elite sales receptionist) */
  successChance: number
}

export function getNegotiationEffects(staff: Staff[]): NegotiationEffects {
  const sales = bestRawSubSkill(staff, 'receptionist', 'ventas')
  return { successChance: 0.35 + sales / 200 }
}

export interface MaintenanceEffects {
  /** Condition points restored per structure per week */
  repairPerWeek: number
}

export function getMaintenanceEffects(staff: Staff[]): MaintenanceEffects {
  const workers = ofType(staff, 'maintenance')
  if (workers.length === 0) return { repairPerWeek: 0 }
  // Parity: best morale-weighted overall skill / 20 (skill 100 → +5/week)
  const best = workers.reduce((max, w) => Math.max(max, effectiveSkill(w.skill, w.happiness)), 0)
  return { repairPerWeek: best / 20 }
}

export interface FrontDeskEffects {
  /** Flat demand-factor bonus from having a receptionist */
  demandBonus: number
  /** Cafeteria retail income multiplier from having a barista */
  cafeMultiplier: number
}

export function getFrontDeskEffects(staff: Staff[]): FrontDeskEffects {
  return {
    demandBonus: staff.some(s => s.type === 'receptionist') ? 0.05 : 0,
    cafeMultiplier: staff.some(s => s.type === 'barista') ? 1.5 : 1,
  }
}

export interface MedicalEffects {
  /** Multiplier on the weekly injury-risk roll: 1.0 → 0.6 with an elite
   *  physio (prevención, morale-weighted) */
  injuryRiskMult: number
  /** Recovery speed: injured players heal `7 × recoveryRate` days per week.
   *  1.0 with no medical staff → 1.8 with elite doctor + physio */
  recoveryRate: number
  /** Extra fatigue recovered per week (nutritionist energía: 0 → +10) */
  fatigueRecoveryBonus: number
  /** Weekly form can't fluctuate below this floor (psychologist presión:
   *  0 → 40 elite). 0 = no floor (identity) */
  formFloor: number
  /** Passive weekly happiness for every staff member (psychologist moral) */
  moralePassive: number
}

function bestEffectiveSubSkill(staff: Staff[], type: StaffType, key: string): number {
  return ofType(staff, type).reduce((max, s) => Math.max(max, effectiveSubSkill(s, key)), 0)
}

export function getMedicalEffects(staff: Staff[]): MedicalEffects {
  const prevention = bestEffectiveSubSkill(staff, 'physio', 'prevencion')
  const physioRecovery = bestEffectiveSubSkill(staff, 'physio', 'recuperacion')
  const doctorTreatment = bestEffectiveSubSkill(staff, 'doctor', 'tratamiento')
  const energy = bestEffectiveSubSkill(staff, 'nutritionist', 'energia')
  const pressure = bestEffectiveSubSkill(staff, 'psychologist', 'presion')
  const morale = bestEffectiveSubSkill(staff, 'psychologist', 'moral')

  return {
    injuryRiskMult: 1 - 0.4 * (prevention / 100),
    recoveryRate: 1 + 0.4 * (physioRecovery / 100) + 0.4 * (doctorTreatment / 100),
    fatigueRecoveryBonus: Math.round(10 * (energy / 100)),
    formFloor: Math.round(40 * (pressure / 100)),
    moralePassive: morale >= 50 ? 1 : 0,
  }
}

// --- Club needs assessment ---------------------------------------------------

export interface ClubNeed {
  id: string
  severity: 'info' | 'warn' | 'urgent'
  message: string
}

export interface ClubNeedsInput {
  staff: Staff[]
  players: Array<{ age: number; level: number; potential: number; injury: unknown | null }>
  courts: Array<{ condition: number }>
  hasLeague: boolean
  leagueMinPlayers: number
  cafeteriaEnabled: boolean
}

/** What the club is missing right now, ranked most urgent first. Pure —
 *  the UI (ClubNeedsBanner) just renders the top entries. */
export function assessClubNeeds(input: ClubNeedsInput): ClubNeed[] {
  const needs: ClubNeed[] = []
  const has = (type: StaffType) => input.staff.some(s => s.type === type)

  const injured = input.players.filter(p => p.injury !== null).length
  if (injured > 0 && !has('physio') && !has('doctor')) {
    needs.push({
      id: 'medical',
      severity: 'urgent',
      message: `${injured} jugador${injured === 1 ? '' : 'es'} lesionado${injured === 1 ? '' : 's'} sin cuerpo médico: un fisio o médico acelera las altas`,
    })
  }

  const healthy = input.players.length - injured
  if (input.hasLeague && healthy < input.leagueMinPlayers) {
    needs.push({
      id: 'lineup',
      severity: 'urgent',
      message: `Solo ${healthy} jugadores disponibles: por debajo de ${input.leagueMinPlayers} la liga se pierde por incomparecencia`,
    })
  }

  const avgCondition = input.courts.length > 0
    ? input.courts.reduce((sum, c) => sum + c.condition, 0) / input.courts.length
    : 100
  if (avgCondition < 70 && !has('maintenance')) {
    needs.push({
      id: 'maintenance',
      severity: 'warn',
      message: 'Las pistas se degradan sin técnico de mantenimiento: la condición media ya baja del 70%',
    })
  }

  const growingTalent = input.players.some(p => p.age < 23 && p.level < p.potential)
  if (growingTalent && !has('coach')) {
    needs.push({
      id: 'coach',
      severity: 'info',
      message: 'Tienes promesas con margen de mejora pero ningún entrenador que las desarrolle',
    })
  }

  if (input.players.length > 0 && !has('physio')) {
    needs.push({
      id: 'prevention',
      severity: 'info',
      message: 'Un fisioterapeuta reduce hasta un 40% el riesgo de lesión de la plantilla',
    })
  }

  if (input.cafeteriaEnabled && !has('barista')) {
    needs.push({
      id: 'barista',
      severity: 'info',
      message: 'La cafetería funciona sin barista: pierdes el 50% extra de ingresos',
    })
  }

  const order = { urgent: 0, warn: 1, info: 2 }
  return needs.sort((a, b) => order[a.severity] - order[b.severity])
}
